import { parseContract } from '../contracts/generated/contracts.js';

const PREFIX = 'corecry.journal.responses.v2:';
const TTL = 30 * 60 * 1000;
const LIMIT = 12;
const MAX_BYTES = 2 * 1024 * 1024;
const memory = new Map();
const contracts = { dashboard: 'journal_dashboard_response', trades: 'journal_trades_response', positions: 'journal_positions_response' };
const storage = () => { try { return globalThis.sessionStorage; } catch { return null; } };
export const journalRequestKey = (section, query) => JSON.stringify([section, Object.entries(query).sort(([a], [b]) => a.localeCompare(b))]);
export const journalToday = (timezone, now = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

function entries(context) {
    if (memory.has(context)) return memory.get(context);
    try {
        const value = JSON.parse(storage()?.getItem(PREFIX + context) || 'null');
        const result = Array.isArray(value) ? value.filter((e) => e && typeof e.key === 'string' && Number.isFinite(e.receivedAt)) : [];
        memory.set(context, result);
        return result;
    } catch { return []; }
}

function persist(context, value) {
    const now = Date.now();
    value = value.filter((e) => now >= e.receivedAt && now - e.receivedAt < TTL).sort((a, b) => b.usedAt - a.usedAt).slice(0, LIMIT);
    while (JSON.stringify(value).length * 2 > MAX_BYTES) value.pop();
    memory.set(context, value);
    try { storage()?.setItem(PREFIX + context, JSON.stringify(value)); } catch { /* Memory remains usable when storage is full or blocked. */ }
}

export function readJournalResponse(context, section, query, clock) {
    if (!context) return null;
    const value = entries(context), key = journalRequestKey(section, query);
    const entry = value.find((e) => e.key === key);
    if (!entry) return null;
    try {
        const age = Date.now() - entry.receivedAt;
        if (age < 0 || age >= TTL || entry.timezone !== clock.timezone || (query.period && entry.today !== clock.today)) throw new Error('Expired report context');
        parseContract(contracts[section], entry.response);
        const filters = entry.response.data.filters;
        if (filters && (filters.timezone !== clock.timezone || (query.period && filters.today !== clock.today))) throw new Error('Expired reporting period');
        entry.usedAt = Date.now();
        persist(context, value);
        return structuredClone(entry);
    } catch {
        persist(context, value.filter((e) => e !== entry));
        return null;
    }
}

export function writeJournalResponse(context, section, query, response, clock) {
    if (!context || query.cursor) return;
    parseContract(contracts[section], response);
    const receivedAt = Date.now(), key = journalRequestKey(section, query);
    if (clock.confirmed === false && !response.data.filters) return;
    const resolved = response.data.filters || clock;
    const entry = { key, response: structuredClone(response), receivedAt, usedAt: receivedAt, timezone: resolved.timezone, today: resolved.today };
    persist(context, [entry, ...entries(context).filter((e) => e.key !== key)]);
}

export function clearJournalResponses(context) {
    if (context) memory.delete(context); else memory.clear();
    try {
        const target = storage();
        if (context) target?.removeItem(PREFIX + context);
        else for (let i = (target?.length || 0) - 1; i >= 0; i--) {
            const key = target.key(i);
            if (key?.startsWith(PREFIX)) target.removeItem(key);
        }
    } catch { /* Optional rendering cache. */ }
}
